import { z } from 'zod';
import {
  AVATAR_KEYS,
  MAX_LIVES,
  MAX_PLAYERS,
  MAX_STARS,
  MIN_LIVES,
  MIN_PLAYERS,
  MIN_STARS,
  NICKNAME_MAX_LENGTH,
  NICKNAME_MIN_LENGTH,
  ROOM_CODE_ALPHABET,
  ROOM_CODE_LENGTH,
} from './constants.js';
import { ERROR_CODES } from './errors.js';
import type { AppErrorPayload } from './errors.js';
import { GAME_PHASES } from './types.js';
import type { PrivatePlayerSnapshot, PublicRoomSnapshot } from './types.js';

/* -------------------------------------------------------------------------- */
/* Primitivos                                                                  */
/* -------------------------------------------------------------------------- */

/** Remove caracteres de controle e colapsa espaços repetidos. */
function normalizeText(input: string): string {
  // eslint-disable-next-line no-control-regex
  const withoutControl = input.replace(/[\u0000-\u001F\u007F]/gu, '');
  return withoutControl.replace(/\s+/g, ' ').trim();
}

export const nicknameSchema = z
  .string()
  .max(200)
  .transform(normalizeText)
  .pipe(z.string().min(NICKNAME_MIN_LENGTH).max(NICKNAME_MAX_LENGTH));

export const roomCodeSchema = z
  .string()
  .max(50)
  .transform((value) => value.trim().toUpperCase())
  .pipe(
    z
      .string()
      .length(ROOM_CODE_LENGTH)
      .regex(new RegExp(`^[${ROOM_CODE_ALPHABET}]+$`), 'INVALID_CODE'),
  );

export const idSchema = z.string().min(1).max(64);
export const normalizedCoordinateSchema = z.number().finite().min(-0.5).max(1.5);
export const avatarKeySchema = z.enum(AVATAR_KEYS);
export const gamePhaseSchema = z.enum(GAME_PHASES);
export const errorCodeSchema = z.enum(ERROR_CODES);

export const appErrorSchema = z.object({
  code: errorCodeSchema,
  message: z.string(),
  details: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});

/* -------------------------------------------------------------------------- */
/* Objetos de domínio (usados nos eventos servidor → cliente)                  */
/* -------------------------------------------------------------------------- */

export const cardSchema = z.object({ id: idSchema, value: z.number().int() });

export const roomConfigSchema = z.object({
  capacity: z.number().int().min(MIN_PLAYERS).max(MAX_PLAYERS),
  maxLevel: z.number().int().min(1).max(50),
  startingLives: z.number().int().min(MIN_LIVES).max(MAX_LIVES),
  startingStars: z.number().int().min(MIN_STARS).max(MAX_STARS),
  soundEnabled: z.boolean(),
  vibrationEnabled: z.boolean(),
  useRecommended: z.boolean(),
});

export const publicPlayerSchema = z.object({
  id: idSchema,
  name: z.string(),
  seat: z.number().int(),
  connected: z.boolean(),
  ready: z.boolean(),
  remainingCards: z.number().int().min(0),
  avatarKey: avatarKeySchema,
  isHost: z.boolean(),
});

export const playedCardSchema = z.object({
  cardId: idSchema,
  value: z.number().int(),
  playerId: idSchema,
  playedAt: z.number(),
  intervalMs: z.number(),
  causedError: z.boolean(),
});

export const revealedCardSchema = z.object({
  value: z.number().int(),
  playerId: idSchema,
  reason: z.enum(['error', 'star']),
  revealedAt: z.number(),
});

export const timerStateSchema = z.object({
  running: z.boolean(),
  intervalAnchor: z.number(),
  intervalFrozenMs: z.number(),
  levelAnchor: z.number(),
  levelAccumulatedMs: z.number(),
});

export const publicDragStateSchema = z.object({
  playerId: idSchema,
  x: z.number(),
  y: z.number(),
  updatedAt: z.number(),
});

export const starVoteStateSchema = z.object({
  requestedBy: idSchema,
  votes: z.record(z.string(), z.boolean()),
  expiresAt: z.number(),
});

export const levelStatsSchema = z.object({
  level: z.number().int(),
  cardsPlayed: z.number().int(),
  errors: z.number().int(),
  totalMs: z.number(),
  averageIntervalMs: z.number(),
});

export const gameStatsSchema = z.object({
  levelsCompleted: z.number().int(),
  cardsPlayed: z.number().int(),
  errors: z.number().int(),
  starsUsed: z.number().int(),
  totalMs: z.number(),
});

const discardGroupSchema = z.object({ playerId: idSchema, values: z.array(z.number().int()) });

export const errorSummarySchema = z.object({
  playedValue: z.number().int(),
  playedBy: idSchema,
  livesRemaining: z.number().int(),
  discarded: z.array(discardGroupSchema),
  at: z.number(),
});

export const starSummarySchema = z.object({
  requestedBy: idSchema,
  starsRemaining: z.number().int(),
  discarded: z.array(discardGroupSchema),
  at: z.number(),
});

export const rewardSummarySchema = z.object({
  level: z.number().int(),
  reward: z.enum(['star', 'life']),
  capped: z.boolean(),
  at: z.number(),
});

export const publicRoomSnapshotSchema = z.object({
  code: z.string(),
  hostPlayerId: z.string(),
  phase: gamePhaseSchema,
  config: roomConfigSchema,
  players: z.array(publicPlayerSchema),
  level: z.number().int(),
  maxLevel: z.number().int(),
  lives: z.number().int(),
  maxLives: z.number().int(),
  stars: z.number().int(),
  maxStars: z.number().int(),
  pile: z.array(playedCardSchema),
  topValue: z.number().int().nullable(),
  playedCount: z.number().int(),
  discardedCards: z.array(revealedCardSchema),
  stateVersion: z.number().int(),
  timer: timerStateSchema.extend({ serverNow: z.number() }),
  countdownEndsAt: z.number().nullable(),
  starVote: starVoteStateSchema.nullable(),
  activeDrags: z.array(publicDragStateSchema),
  lastError: errorSummarySchema.nullable(),
  lastStar: starSummarySchema.nullable(),
  lastReward: rewardSummarySchema.nullable(),
  lastLevelSummary: levelStatsSchema.nullable(),
  gameStats: gameStatsSchema,
  reconnectDeadline: z.number().nullable(),
  canEndGame: z.boolean(),
});

export const privatePlayerSnapshotSchema = z.object({
  playerId: idSchema,
  seat: z.number().int(),
  avatarKey: avatarKeySchema,
  isHost: z.boolean(),
  ready: z.boolean(),
  hand: z.array(cardSchema),
  playableCardId: idSchema.nullable(),
  stateVersion: z.number().int(),
});

/**
 * Verificações em tempo de compilação: se um esquema divergir do tipo de
 * domínio correspondente, o `typecheck` falha.
 */
export const _snapshotContractChecks: {
  publicToSchema: (value: PublicRoomSnapshot) => z.infer<typeof publicRoomSnapshotSchema>;
  publicFromSchema: (value: z.infer<typeof publicRoomSnapshotSchema>) => PublicRoomSnapshot;
  privateToSchema: (value: PrivatePlayerSnapshot) => z.infer<typeof privatePlayerSnapshotSchema>;
  privateFromSchema: (value: z.infer<typeof privatePlayerSnapshotSchema>) => PrivatePlayerSnapshot;
} = {
  publicToSchema: (value) => value,
  publicFromSchema: (value) => value,
  privateToSchema: (value) => value,
  privateFromSchema: (value) => value,
};

/* -------------------------------------------------------------------------- */
/* Cliente → servidor                                                          */
/* -------------------------------------------------------------------------- */

export const roomConfigPatchSchema = z
  .object({
    capacity: z.number().int().min(MIN_PLAYERS).max(MAX_PLAYERS).optional(),
    maxLevel: z.number().int().min(1).max(50).optional(),
    startingLives: z.number().int().min(MIN_LIVES).max(MAX_LIVES).optional(),
    startingStars: z.number().int().min(MIN_STARS).max(MAX_STARS).optional(),
    soundEnabled: z.boolean().optional(),
    vibrationEnabled: z.boolean().optional(),
    useRecommended: z.boolean().optional(),
  })
  .strict();

export const roomCreatePayloadSchema = z
  .object({
    name: nicknameSchema,
    capacity: z.number().int().min(MIN_PLAYERS).max(MAX_PLAYERS),
    config: roomConfigPatchSchema.optional(),
  })
  .strict();

export const roomJoinPayloadSchema = z
  .object({ code: roomCodeSchema, name: nicknameSchema })
  .strict();

export const roomReconnectPayloadSchema = z
  .object({ code: roomCodeSchema, playerId: idSchema, sessionToken: z.string().min(16).max(256) })
  .strict();

export const emptyPayloadSchema = z.object({}).strict();
export const roomUpdateConfigPayloadSchema = z.object({ patch: roomConfigPatchSchema }).strict();
export const roomRemovePlayerPayloadSchema = z.object({ playerId: idSchema }).strict();
export const playerReadyPayloadSchema = z.object({ ready: z.boolean() }).strict();
export const dragPositionPayloadSchema = z
  .object({ x: normalizedCoordinateSchema, y: normalizedCoordinateSchema })
  .strict();
export const cardPlayPayloadSchema = z.object({ cardId: idSchema }).strict();
export const starVotePayloadSchema = z.object({ approve: z.boolean() }).strict();
export const timeSyncPayloadSchema = z.object({ t0: z.number() }).strict();

export type RoomCreatePayload = z.infer<typeof roomCreatePayloadSchema>;
export type RoomJoinPayload = z.infer<typeof roomJoinPayloadSchema>;
export type RoomReconnectPayload = z.infer<typeof roomReconnectPayloadSchema>;
export type RoomUpdateConfigPayload = z.infer<typeof roomUpdateConfigPayloadSchema>;
export type RoomRemovePlayerPayload = z.infer<typeof roomRemovePlayerPayloadSchema>;
export type PlayerReadyPayload = z.infer<typeof playerReadyPayloadSchema>;
export type DragPositionPayload = z.infer<typeof dragPositionPayloadSchema>;
export type CardPlayPayload = z.infer<typeof cardPlayPayloadSchema>;
export type StarVotePayload = z.infer<typeof starVotePayloadSchema>;
export type TimeSyncPayload = z.infer<typeof timeSyncPayloadSchema>;
export type RoomConfigPatch = z.infer<typeof roomConfigPatchSchema>;
export type EmptyPayload = z.infer<typeof emptyPayloadSchema>;

/** Registro central: usado pelo servidor para validar cada evento recebido. */
export const CLIENT_EVENT_SCHEMAS = {
  'room:create': roomCreatePayloadSchema,
  'room:join': roomJoinPayloadSchema,
  'room:reconnect': roomReconnectPayloadSchema,
  'room:leave': emptyPayloadSchema,
  'room:start': emptyPayloadSchema,
  'room:updateConfig': roomUpdateConfigPayloadSchema,
  'room:removePlayer': roomRemovePlayerPayloadSchema,
  'room:endGame': emptyPayloadSchema,
  'player:ready': playerReadyPayloadSchema,
  'player:requestRefocus': emptyPayloadSchema,
  'drag:start': dragPositionPayloadSchema,
  'drag:move': dragPositionPayloadSchema,
  'drag:cancel': emptyPayloadSchema,
  'card:play': cardPlayPayloadSchema,
  'star:request': emptyPayloadSchema,
  'star:vote': starVotePayloadSchema,
  'game:playAgain': emptyPayloadSchema,
  'time:sync': timeSyncPayloadSchema,
} as const;

export type ClientEventName = keyof typeof CLIENT_EVENT_SCHEMAS;

/* -------------------------------------------------------------------------- */
/* Servidor → cliente                                                          */
/* -------------------------------------------------------------------------- */

export const sessionCreatedSchema = z.object({
  code: z.string(),
  playerId: idSchema,
  sessionToken: z.string(),
});

export const phaseChangedSchema = z.object({
  phase: gamePhaseSchema,
  previous: gamePhaseSchema,
  stateVersion: z.number().int(),
});

export const countdownStartedSchema = z.object({ endsAt: z.number(), serverNow: z.number() });
export const timerAnchorSchema = timerStateSchema.extend({ serverNow: z.number() });
export const dragStartedSchema = z.object({ playerId: idSchema, x: z.number(), y: z.number() });
export const dragMovedSchema = dragStartedSchema;
export const dragEndedSchema = z.object({
  playerId: idSchema,
  reason: z.enum(['cancel', 'played', 'disconnect', 'phase']),
});

export const cardAcceptedSchema = z.object({
  cardId: idSchema,
  value: z.number().int(),
  playerId: idSchema,
  causedError: z.boolean(),
  intervalMs: z.number(),
  playedCount: z.number().int(),
});

export const cardRejectedSchema = z.object({ cardId: idSchema, error: appErrorSchema });

export const starVoteStartedSchema = z.object({
  requestedBy: idSchema,
  expiresAt: z.number(),
  serverNow: z.number(),
});

export const starVoteUpdatedSchema = z.object({
  votes: z.record(z.string(), z.boolean()),
  pending: z.array(idSchema),
});

export const starResolvedSchema = z.object({
  approved: z.boolean(),
  summary: starSummarySchema.nullable(),
});

export const gameWonSchema = z.object({ stats: gameStatsSchema });
export const gameLostSchema = z.object({ level: z.number().int(), stats: gameStatsSchema });
export const connectionPausedSchema = z.object({
  playerId: idSchema,
  reconnectDeadline: z.number().nullable(),
});
export const connectionRestoredSchema = z.object({ playerId: idSchema });
export const playersUpdatedSchema = z.object({ players: z.array(publicPlayerSchema) });

export const SERVER_EVENT_SCHEMAS = {
  'room:created': sessionCreatedSchema,
  'room:joined': sessionCreatedSchema,
  'room:snapshot': publicRoomSnapshotSchema,
  'player:privateState': privatePlayerSnapshotSchema,
  'room:error': appErrorSchema,
  'players:updated': playersUpdatedSchema,
  'phase:changed': phaseChangedSchema,
  'countdown:started': countdownStartedSchema,
  'timer:anchor': timerAnchorSchema,
  'drag:started': dragStartedSchema,
  'drag:moved': dragMovedSchema,
  'drag:ended': dragEndedSchema,
  'card:accepted': cardAcceptedSchema,
  'card:rejected': cardRejectedSchema,
  'error:resolved': errorSummarySchema,
  'star:voteStarted': starVoteStartedSchema,
  'star:voteUpdated': starVoteUpdatedSchema,
  'star:resolved': starResolvedSchema,
  'level:completed': levelStatsSchema,
  'reward:granted': rewardSummarySchema,
  'game:won': gameWonSchema,
  'game:lost': gameLostSchema,
  'connection:paused': connectionPausedSchema,
  'connection:restored': connectionRestoredSchema,
} as const;

export type ServerEventName = keyof typeof SERVER_EVENT_SCHEMAS;

export type SessionCreated = z.infer<typeof sessionCreatedSchema>;
export type PhaseChanged = z.infer<typeof phaseChangedSchema>;
export type CountdownStarted = z.infer<typeof countdownStartedSchema>;
export type TimerAnchor = z.infer<typeof timerAnchorSchema>;
export type DragStarted = z.infer<typeof dragStartedSchema>;
export type DragMoved = z.infer<typeof dragMovedSchema>;
export type DragEnded = z.infer<typeof dragEndedSchema>;
export type CardAccepted = z.infer<typeof cardAcceptedSchema>;
export type CardRejected = z.infer<typeof cardRejectedSchema>;
export type StarVoteStarted = z.infer<typeof starVoteStartedSchema>;
export type StarVoteUpdated = z.infer<typeof starVoteUpdatedSchema>;
export type StarResolved = z.infer<typeof starResolvedSchema>;
export type GameWon = z.infer<typeof gameWonSchema>;
export type GameLost = z.infer<typeof gameLostSchema>;
export type ConnectionPaused = z.infer<typeof connectionPausedSchema>;
export type ConnectionRestored = z.infer<typeof connectionRestoredSchema>;
export type PlayersUpdated = z.infer<typeof playersUpdatedSchema>;

/* -------------------------------------------------------------------------- */
/* Tipagem dos sockets                                                         */
/* -------------------------------------------------------------------------- */

export type Ack<T> = { ok: true; data: T } | { ok: false; error: AppErrorPayload };
export type AckFn<T> = (response: Ack<T>) => void;

export type ClientToServerEvents = {
  'room:create': (payload: RoomCreatePayload, ack: AckFn<SessionCreated>) => void;
  'room:join': (payload: RoomJoinPayload, ack: AckFn<SessionCreated>) => void;
  'room:reconnect': (payload: RoomReconnectPayload, ack: AckFn<SessionCreated>) => void;
  'room:leave': (payload: EmptyPayload, ack: AckFn<null>) => void;
  'room:start': (payload: EmptyPayload, ack: AckFn<null>) => void;
  'room:updateConfig': (payload: RoomUpdateConfigPayload, ack: AckFn<null>) => void;
  'room:removePlayer': (payload: RoomRemovePlayerPayload, ack: AckFn<null>) => void;
  'room:endGame': (payload: EmptyPayload, ack: AckFn<null>) => void;
  'player:ready': (payload: PlayerReadyPayload, ack: AckFn<null>) => void;
  'player:requestRefocus': (payload: EmptyPayload, ack: AckFn<null>) => void;
  'drag:start': (payload: DragPositionPayload) => void;
  'drag:move': (payload: DragPositionPayload) => void;
  'drag:cancel': (payload: EmptyPayload) => void;
  'card:play': (payload: CardPlayPayload, ack: AckFn<null>) => void;
  'star:request': (payload: EmptyPayload, ack: AckFn<null>) => void;
  'star:vote': (payload: StarVotePayload, ack: AckFn<null>) => void;
  'game:playAgain': (payload: EmptyPayload, ack: AckFn<null>) => void;
  'time:sync': (payload: TimeSyncPayload, ack: AckFn<{ serverNow: number }>) => void;
};

export type ServerToClientEvents = {
  'room:created': (payload: SessionCreated) => void;
  'room:joined': (payload: SessionCreated) => void;
  'room:snapshot': (payload: PublicRoomSnapshot) => void;
  'player:privateState': (payload: PrivatePlayerSnapshot) => void;
  'room:error': (payload: AppErrorPayload) => void;
  'players:updated': (payload: PlayersUpdated) => void;
  'phase:changed': (payload: PhaseChanged) => void;
  'countdown:started': (payload: CountdownStarted) => void;
  'timer:anchor': (payload: TimerAnchor) => void;
  'drag:started': (payload: DragStarted) => void;
  'drag:moved': (payload: DragMoved) => void;
  'drag:ended': (payload: DragEnded) => void;
  'card:accepted': (payload: CardAccepted) => void;
  'card:rejected': (payload: CardRejected) => void;
  'error:resolved': (payload: z.infer<typeof errorSummarySchema>) => void;
  'star:voteStarted': (payload: StarVoteStarted) => void;
  'star:voteUpdated': (payload: StarVoteUpdated) => void;
  'star:resolved': (payload: StarResolved) => void;
  'level:completed': (payload: z.infer<typeof levelStatsSchema>) => void;
  'reward:granted': (payload: z.infer<typeof rewardSummarySchema>) => void;
  'game:won': (payload: GameWon) => void;
  'game:lost': (payload: GameLost) => void;
  'connection:paused': (payload: ConnectionPaused) => void;
  'connection:restored': (payload: ConnectionRestored) => void;
};
